import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useThemeColors, useThemeFonts } from '@brandclub/bc-widgets';

/**
 * Resolve the brand's body font to a CSS `font-family` string, mirroring how
 * `ConciergeContainer` derives `brandFontFamily` for the text surface. Returns
 * `undefined` when no brand font is configured so callers can fall through to
 * inherited defaults.
 */
function resolveBodyFontFamily(
  bodyFont: ReturnType<typeof useThemeFonts>['themeFonts']['body'] | undefined,
): string | undefined {
  if (!bodyFont) return undefined;
  return bodyFont.format === 'system' ? bodyFont.url : `"${bodyFont.name}", sans-serif`;
}

import { useVoiceSession, type VoiceTranscriptLine, type VoiceWidgetTimedEvent } from './hooks/useVoiceSession';
import { useVoiceSoundEffects } from './hooks/useVoiceSoundEffects';
import { useIsCompactVoiceLayout } from './hooks/useIsCompactVoiceLayout';
import { VoiceBlob, type BlobState } from './components/Blob';
import { ControlBar, type ControlBarMode } from './components/ControlBar';
import { VoiceBottomDock } from './components/VoiceBottomDock';
import { VoiceScrollList } from './components/VoiceScrollList';
import { VoiceThemeProvider, useVoiceThemeContext } from './theme/VoiceThemeProvider';
import { ConfirmActionDialog } from '../components/ConfirmActionDialog';

/**
 * VoiceCallOverlay — decisions/078 (single-scroll, themed).
 *
 * Full-screen voice call surface rendered ABOVE the unified Concierge
 * route. Bottom bar matches Figma `4936:3489`: text input, mic mute, and
 * brand-colored End / Cancel / Send pill. Blob animation is optional via
 * `voiceBranding.visual.blobEnabled` (Studio → Voice).
 *
 * Connecting: no centered status/blob — optional docked blob only when
 * enabled; footer shows input + mic + Cancel (spinner).
 */

export interface VoiceCallOverlayProps {
  conversationId: string;
  open: boolean;
  onClose: (info: {
    reason: string;
    durationSec?: number;
    lines?: VoiceTranscriptLine[];
    widgetEvents?: VoiceWidgetTimedEvent[];
  }) => void;
  /** PDP product SKU — forwarded to `POST /concierge/voice/session` when on a product page. */
  pdpStacklineSku?: string;
  /**
   * When true, the overlay positions itself with `position: absolute; inset: 0` and
   * inherits the parent's `border-radius` — used by the chat-bubble surface so the
   * voice UI fills only the bubble container (not the viewport). Default `false`
   * preserves the fullscreen behavior used by the dedicated `/concierge` page.
   */
  containerScoped?: boolean;
}

// Stable empty references so the connecting-phase `VoiceScrollList` doesn't
// re-run its merge `useMemo` on every parent render.
const EMPTY_LINES: VoiceTranscriptLine[] = [];
const EMPTY_WIDGET_EVENTS: VoiceWidgetTimedEvent[] = [];

function deriveBlobState(args: {
  phase: ReturnType<typeof useVoiceSession>['phase'];
  muted: boolean;
  isUserSpeaking: boolean;
  hasStatus: boolean;
}): BlobState {
  if (args.phase === 'error') return 'error';
  if (args.phase === 'ended') return 'ended';
  if (args.phase === 'requesting-permission' || args.phase === 'connecting' || args.phase === 'ending') {
    return 'connecting';
  }
  if (args.phase !== 'live') return 'connecting';
  if (args.muted) return 'muted';
  if (args.hasStatus) return 'tool';
  if (args.isUserSpeaking) return 'listening';
  return 'idle';
}

function deriveControlBarMode(phase: ReturnType<typeof useVoiceSession>['phase']): ControlBarMode {
  if (phase === 'live') return 'live';
  if (phase === 'ending') return 'ending';
  return 'connecting';
}

// ─── Inner: live call shell ─────────────────────────────────────────────────

const VoiceCallOverlayInner: React.FC<{
  conversationId: string;
  pdpStacklineSku?: string;
  onEnded: (info: {
    reason: string;
    durationSec?: number;
    mastraThreadCreated?: boolean;
    lines?: VoiceTranscriptLine[];
    widgetEvents?: VoiceWidgetTimedEvent[];
  }) => void;
  onUserEndClick: () => void;
}> = ({ conversationId, pdpStacklineSku, onEnded, onUserEndClick }) => {
  const errorHeadline = 'Voice mode unavailable';
  const { visual } = useVoiceThemeContext();
  const blobEnabled = visual?.blobEnabled ?? false;

  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  const dialogBg = themeColors.background || '#ffffff';
  const dialogPrimary = themeColors.textPrimary || themeColors.primary || '#0d2340';
  const dialogPrimaryFg = themeColors.buttonText || '#ffffff';
  const dialogBodyFont = themeFonts?.body;
  const dialogFontFamily = dialogBodyFont
    ? dialogBodyFont.format === 'system'
      ? dialogBodyFont.url
      : `"${dialogBodyFont.name}", sans-serif`
    : undefined;

  const [inputText, setInputText] = useState('');

  const sfx = useVoiceSoundEffects();

  const handleEnded = useCallback(
    (info: {
      reason: string;
      durationSec?: number;
      mastraThreadCreated?: boolean;
      lines?: VoiceTranscriptLine[];
      widgetEvents?: VoiceWidgetTimedEvent[];
    }) => {
      if (info.reason !== 'ended_by_model') {
        sfx.playEndCall();
      }
      onEnded(info);
    },
    [onEnded, sfx],
  );

  const session = useVoiceSession({
    conversationId,
    pdpStacklineSku,
    onEnded: handleEnded,
    onEndCallInitiated: () => sfx.playEndCall(),
  });

  const toolStatus = session.lastStatusEvent?.data?.status;

  // D-89 — show the status line the instant a tool round-trip starts
  // (`toolCallsInFlight`, same local signal that drives the SFX loop) rather
  // than waiting for the backend's `data-status` SSE, which lags 500-1000ms.
  // Use the specific status text once it arrives; fall back to a generic
  // "Thinking…" in the meantime.
  const pendingStatus =
    session.phase === 'live' && (session.toolCallsInFlight > 0 || !!toolStatus) ? toolStatus || 'Thinking…' : null;

  // Reuse the tool-call shimmer ("Thinking…") to signal that the call is
  // connecting — otherwise the only cue is the footer's Cancel spinner, which
  // doesn't read as "connecting". Rendered inline above the footer via the
  // same `VoiceScrollList` status row.
  const connectingStatus =
    session.phase === 'requesting-permission' || session.phase === 'connecting' ? 'Connecting…' : null;

  const shouldPlayToolLoop = session.phase === 'live' && session.toolCallsInFlight > 0 && !session.isAssistantAudible;

  useEffect(() => {
    if (shouldPlayToolLoop) {
      sfx.startToolLoop();
    } else {
      sfx.stopToolLoop();
    }
  }, [shouldPlayToolLoop, sfx]);

  const blobState = useMemo<BlobState>(
    () =>
      deriveBlobState({
        phase: session.phase,
        muted: session.muted,
        isUserSpeaking: session.isUserSpeaking,
        hasStatus: !!toolStatus,
      }),
    [session.phase, session.muted, session.isUserSpeaking, toolStatus],
  );

  const controlBarMode = deriveControlBarMode(session.phase);

  const handleEnd = useCallback(async () => {
    await session.end('user_ended');
  }, [session]);

  const handleSubmitText = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || session.phase !== 'live') return;
    session.sendText(trimmed);
    setInputText('');
  }, [inputText, session]);

  // D-89 — keep the End-button waveform animating until the WebRTC audio
  // buffer actually stops (`output_audio_buffer.stopped`), not just until
  // `response.done`. `isAssistantAudible` is the buffer-driven signal.
  const waveformActive = session.isAssistantAudible || session.isUserSpeaking || session.toolCallsInFlight > 0;

  return (
    <div style={pageStyle}>
      <div style={liveScrollWrapStyle}>
        {session.phase === 'live' ? (
          <VoiceScrollList
            lines={session.lines}
            widgetEvents={session.widgetEvents}
            pendingStatus={pendingStatus}
            onLoginCompleted={session.notifyLoginCompleted}
            onLoginFailed={session.notifyLoginFailed}
            hasBottomDock={blobEnabled}
          />
        ) : connectingStatus ? (
          <VoiceScrollList lines={EMPTY_LINES} widgetEvents={EMPTY_WIDGET_EVENTS} pendingStatus={connectingStatus} />
        ) : null}
        {blobEnabled ? <VoiceBottomDock blobState={blobState} /> : null}
      </div>

      <ControlBar
        mode={controlBarMode}
        value={inputText}
        onChange={setInputText}
        onSubmit={handleSubmitText}
        muted={session.muted}
        onToggleMute={() => session.setMuted(!session.muted)}
        onEnd={handleEnd}
        active={waveformActive}
        toolActive={session.toolCallsInFlight > 0}
        micDisabled={session.phase !== 'live'}
      />

      <ConfirmActionDialog
        open={session.phase === 'error'}
        title={session.errorTitle ?? errorHeadline}
        message={session.error ?? 'Could not start a call.'}
        confirmText="Reconnect"
        cancelText="Start a new chat"
        backgroundColor={dialogBg}
        primaryColor={dialogPrimary}
        primaryForegroundColor={dialogPrimaryFg}
        fontFamily={dialogFontFamily}
        onConfirm={session.retry}
        onCancel={onUserEndClick}
      />
    </div>
  );
};

// ─── Outer: overlay shell ───────────────────────────────────────────────────

// D-90 — cross-fade duration for the overlay->chat handoff. The chat behind is
// already mounted (and seeded with the just-spoken lines on close), so fading
// the overlay out reveals it in place instead of an instant unmount flash.
const OVERLAY_FADE_MS = 200;

export const VoiceCallOverlay: React.FC<VoiceCallOverlayProps> = ({
  conversationId,
  open,
  onClose,
  pdpStacklineSku,
  containerScoped = false,
}) => {
  const { themeFonts } = useThemeFonts();

  // Local exit state: when the call ends we paint one opacity transition to 0
  // before invoking the parent `onClose` (which unmounts the overlay), so the
  // seeded chat behind cross-fades into view rather than popping.
  const [closing, setClosing] = useState(false);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== undefined) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const fadeThenClose = useCallback(
    (info: Parameters<VoiceCallOverlayProps['onClose']>[0]) => {
      if (closeTimerRef.current !== undefined) return; // already exiting
      setClosing(true);
      closeTimerRef.current = setTimeout(() => onClose(info), OVERLAY_FADE_MS);
    },
    [onClose],
  );

  if (!open) return null;
  if (!conversationId) return null;

  const brandFontFamily = resolveBodyFontFamily(themeFonts?.body);

  const baseShellStyle: React.CSSProperties = containerScoped
    ? {
        position: 'absolute',
        inset: 0,
        // Must sit above ALL chat-column content. The overlay is spatially
        // confined to the column (inset:0), so a high z-index can't bleed onto
        // the sidebar/header (separate DOM regions). zIndex:1 was too low — the
        // federated compare-products widget's sticky attribute/label columns
        // use z-index:2 and poked through; the desktop share button uses 50.
        zIndex: 100,
        background: 'var(--color-background, #FFFFFF)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 'inherit',
        overflow: 'hidden',
        ...(brandFontFamily ? { fontFamily: brandFontFamily } : {}),
      }
    : {
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'var(--color-background, #FFFFFF)',
        display: 'flex',
        flexDirection: 'column',
        ...(brandFontFamily ? { fontFamily: brandFontFamily } : {}),
      };

  const shellStyle: React.CSSProperties = {
    ...baseShellStyle,
    opacity: closing ? 0 : 1,
    transition: `opacity ${OVERLAY_FADE_MS}ms ease`,
    // Don't intercept clicks meant for the revealed chat while fading out.
    pointerEvents: closing ? 'none' : undefined,
  };

  return (
    <VoiceThemeProvider>
      <div style={shellStyle} role="dialog" aria-modal="true" aria-label="Voice call">
        <VoiceCallOverlayInner
          conversationId={conversationId}
          pdpStacklineSku={pdpStacklineSku}
          onEnded={(info) =>
            fadeThenClose({
              reason: info.reason,
              durationSec: info.durationSec,
              lines: info.lines,
              widgetEvents: info.widgetEvents,
            })
          }
          onUserEndClick={() => fadeThenClose({ reason: 'user_dismissed_error' })}
        />
      </div>
    </VoiceThemeProvider>
  );
};

const pageStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
  background: 'transparent',
};

const liveScrollWrapStyle: React.CSSProperties = {
  position: 'relative',
  flex: '1 1 auto',
  minHeight: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export default VoiceCallOverlay;
